import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, Stack, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MenuList from './MenuList';
import TodayCareStatsCards from './MenuCardsTodayCareStats';
import LogoSection from '../LogoSection';
import { IMPERSONATION_BANNER_HEIGHT } from '../ImpersonationBanner';
import useAuth from 'hooks/useAuth';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';
import { drawerWidth } from 'store/constant';
import Chip from 'ui-component/extended/Chip';

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = ({ window }) => {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    // The impersonation banner grows the fixed header — the persistent drawer's
    // hardcoded 88px top/height offsets have to grow with it.
    const { impersonation } = useAuth();
    const headerOffset = impersonation ? 88 + IMPERSONATION_BANNER_HEIGHT : 88;

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);

    const logo = useMemo(
        () => (
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
                    <LogoSection />
                </Box>
            </Box>
        ),
        []
    );

    const drawer = useMemo(
        () => (
            <PerfectScrollbar
                component="div"
                style={{
                    // Below md the drawer is temporary (full-height overlay), so only
                    // the persistent md+ variant tracks the header offset.
                    height: !matchUpMd ? 'calc(100vh - 56px)' : `calc(100vh - ${headerOffset}px)`,
                    paddingLeft: '16px',
                    paddingRight: '16px'
                }}
            >
                <MenuList />
                <TodayCareStatsCards />
                <Stack direction="row" justifyContent="center" sx={{ mb: 2 }}>
                    <Chip
                        label={`v${process.env.REACT_APP_VERSION}.${process.env.REACT_APP_GIT_HASH}`}
                        disabled
                        chipcolor="secondary"
                        size="small"
                        sx={{ cursor: 'pointer' }}
                    />
                </Stack>
            </PerfectScrollbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [matchUpMd, headerOffset]
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    return (
        <Box
            component="nav"
            data-testid="app-sidebar"
            sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }}
            aria-label="mailbox folders"
        >
            <Drawer
                container={container}
                variant={matchUpMd ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={() => dispatch(openDrawer(!drawerOpen))}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        background: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderRight: 'none',
                        [theme.breakpoints.up('md')]: {
                            top: `${headerOffset}px`
                        }
                    }
                }}
                ModalProps={{ keepMounted: true }}
                color="inherit"
            >
                {drawerOpen && logo}
                {drawerOpen && drawer}
            </Drawer>
        </Box>
    );
};

Sidebar.propTypes = {
    window: PropTypes.object
};

export default memo(Sidebar);
